import { List, useTable } from "@refinedev/antd";
import { Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";

import { DATE_TIME_FORMAT, QUESTION_REPORT_REASONS, REPORT_STATUS } from "../labels";
import type { QuestionReport } from "../types";

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Açık" },
  { value: "RESOLVED", label: "Sonuçlanan" },
  { value: "ALL", label: "Tümü" },
];

/**
 * /question-reports: answers of "Soru Çözüm" students reported, open ones oldest first
 * (permission `questions.moderate`).
 */
export function QuestionReportsPage() {
  const navigate = useNavigate();
  const { tableProps, filters, setFilters } = useTable<QuestionReport>({
    resource: "question-reports",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "status", operator: "eq", value: "OPEN" }] },
    syncWithLocation: true,
  });
  const status =
    (filters.find((item) => "field" in item && item.field === "status")?.value as string) ?? "OPEN";

  return (
    <List title="Bildirilen soru çözümleri">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value) =>
            setFilters([{ field: "status", operator: "eq", value: String(value) }], "replace")
          }
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          onClick: () => navigate(`/question-reports/${record.id}`),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column<QuestionReport>
          title="Soru"
          key="question"
          render={(_, report) => (
            <Space direction="vertical" size={0}>
              <Link to={`/question-reports/${report.id}`}>
                <Typography.Text strong>{report.subjectName}</Typography.Text>
              </Link>
              <Typography.Text type="secondary">
                {report.student.name} → {report.tutor?.name ?? "—"}
              </Typography.Text>
            </Space>
          )}
        />
        <Table.Column<QuestionReport>
          title="Neden"
          dataIndex="reason"
          render={(reason: string, report) => (
            <Space direction="vertical" size={0}>
              {QUESTION_REPORT_REASONS[reason]}
              {report.note ? (
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
                  {report.note}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<QuestionReport>
          title="Tarih"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<QuestionReport>
          title="Durum"
          dataIndex="status"
          render={(value: string) => (
            <Tag color={REPORT_STATUS[value]?.color}>{REPORT_STATUS[value]?.label}</Tag>
          )}
        />
      </Table>
    </List>
  );
}
